import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface TimePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (time: string) => void;
  initialTime?: string;
  label: string;
}
export function TimePicker({
  isOpen,
  onClose,
  onSelect,
  initialTime = '9:00 AM',
  label
}: TimePickerProps) {
  const [hour, setHour] = useState(parseInt(initialTime.split(':')[0]));
  const [minute, setMinute] = useState(parseInt(initialTime.split(':')[1].split(' ')[0]));
  const [period, setPeriod] = useState(initialTime.includes('AM') ? 'AM' : 'PM');
  const hours = Array.from({
    length: 12
  }, (_, i) => i + 1);
  const minutes = Array.from({
    length: 12
  }, (_, i) => i * 5);
  // Update time when modal closes
  const handleClose = () => {
    const formattedTime = `${hour}:${minute.toString().padStart(2, '0')} ${period}`;
    onSelect(formattedTime);
    onClose();
  };
  return <AnimatePresence data-id="element-465">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleClose} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" data-id="element-466" />

          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-h-[70vh]" data-id="element-467">
            <div className="flex items-center justify-between px-6 py-4 border-b border-sand/50" data-id="element-468">
              <h2 className="text-xl font-bold text-charcoal" data-id="element-469">{label}</h2>
              <button onClick={handleClose} className="p-2 hover:bg-sand/30 rounded-full transition-colors" data-id="element-470">
                <X size={24} className="text-charcoal" data-id="element-471" />
              </button>
            </div>

            <div className="p-6" data-id="element-472">
              <div className="flex items-center justify-center gap-4 mb-6" data-id="element-473">
                {/* Hour Picker */}
                <div className="flex-1 max-w-[100px]" data-id="element-474">
                  <div className="h-48 overflow-y-auto scrollbar-hide relative" data-id="element-475">
                    <div className="absolute inset-0 pointer-events-none" data-id="element-476">
                      <div className="absolute top-1/2 left-0 right-0 h-12 -translate-y-1/2 bg-dark-green/10 rounded-ios" data-id="element-477" />
                    </div>
                    <div className="py-16" data-id="element-478">
                      {hours.map(h => <button key={h} onClick={() => setHour(h)} className={`w-full py-3 text-center transition-all ${hour === h ? 'text-dark-green font-bold text-2xl' : 'text-moss-gray text-lg'}`} data-id="element-479">
                          {h}
                        </button>)}
                    </div>
                  </div>
                </div>

                <span className="text-2xl font-bold text-charcoal" data-id="element-480">:</span>

                {/* Minute Picker */}
                <div className="flex-1 max-w-[100px]" data-id="element-481">
                  <div className="h-48 overflow-y-auto scrollbar-hide relative" data-id="element-482">
                    <div className="absolute inset-0 pointer-events-none" data-id="element-483">
                      <div className="absolute top-1/2 left-0 right-0 h-12 -translate-y-1/2 bg-dark-green/10 rounded-ios" data-id="element-484" />
                    </div>
                    <div className="py-16" data-id="element-485">
                      {minutes.map(m => <button key={m} onClick={() => setMinute(m)} className={`w-full py-3 text-center transition-all ${minute === m ? 'text-dark-green font-bold text-2xl' : 'text-moss-gray text-lg'}`} data-id="element-486">
                          {m.toString().padStart(2, '0')}
                        </button>)}
                    </div>
                  </div>
                </div>

                {/* AM/PM Picker */}
                <div className="flex-1 max-w-[80px]" data-id="element-487">
                  <div className="h-48 overflow-y-auto scrollbar-hide relative" data-id="element-488">
                    <div className="absolute inset-0 pointer-events-none" data-id="element-489">
                      <div className="absolute top-1/2 left-0 right-0 h-12 -translate-y-1/2 bg-dark-green/10 rounded-ios" data-id="element-490" />
                    </div>
                    <div className="py-16" data-id="element-491">
                      {['AM', 'PM'].map(p => <button key={p} onClick={() => setPeriod(p)} className={`w-full py-3 text-center transition-all ${period === p ? 'text-dark-green font-bold text-2xl' : 'text-moss-gray text-lg'}`} data-id="element-492">
                          {p}
                        </button>)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Current Selection Display */}
              <div className="text-center mb-4" data-id="element-493">
                <p className="text-sm text-moss-gray mb-1" data-id="element-494">Selected time</p>
                <p className="text-2xl font-bold text-dark-green" data-id="element-495">
                  {hour}:{minute.toString().padStart(2, '0')} {period}
                </p>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}